import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, NgZone, OnInit, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { BkSelectComponent } from '../../../../../components/inputs/bk-select/bk-select.component';
import {
  DeliveryReportService,
  SentBucket,
  SentInterval,
  SentOverview,
} from '../../../../../services/reports/delivery-report.service';
import { deliveredShare, formatShare } from '../../../../../services/message/delivery.service';
import { LiveRefresh } from '../../../../../services/live/live-refresh';
import { formatCount } from '../../../../../../utils/sms';
import {
  Period,
  SENT_INTERVALS,
  SENT_PRESETS,
  SentPreset,
  autoInterval,
  axisLabel,
  bucketLabel,
  dateInput,
  fromInputs,
  intervalFits,
  periodLabel,
  presetPeriod,
  reachesNow,
  timeInput,
} from './sent-periods';

/** The viewer's last choice, kept in this browser only. */
const STORAGE_KEY = 'bk.dashboard.sentOverview';
/** While the period runs up to now, look for new sends this often. */
const LIVE_MS = 60_000;
/** Labels along the axis, at most. */
const TICKS = 6;

interface SavedChoice {
  preset?: SentPreset;
  interval?: SentInterval | null;
  from?: string;
  to?: string;
}

/**
 * "Messages sent": how many SMS went out from the portal in a period the viewer picks, split into
 * delivered, failed and no receipt yet, with a bar per hour, day, week or month.
 */
@Component({
  selector: 'dash-sent-overview',
  standalone: true,
  imports: [BkSelectComponent],
  templateUrl: './sent-overview.component.html',
  styleUrl: './sent-overview.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { role: 'region', 'aria-labelledby': 'so-title' },
})
export class SentOverviewComponent implements OnInit {
  private reports = inject(DeliveryReportService);
  private cdr = inject(ChangeDetectorRef);
  private zone = inject(NgZone);
  private destroyRef = inject(DestroyRef);

  readonly presetOptions = SENT_PRESETS.map(({ value, label }) => ({ value, label }));
  readonly intervalOptions = SENT_INTERVALS;
  readonly formatCount = formatCount;

  preset: SentPreset = 'last7';
  /** The viewer's pick; null follows the period. */
  private picked: SentInterval | null = null;
  interval: SentInterval = 'day';
  period: Period = presetPeriod('last7');

  customFromDate = '';
  customFromTime = '00:00';
  customToDate = '';
  customToTime = '';
  rangeError = '';

  data: SentOverview | null = null;
  buckets: SentBucket[] = [];
  /** The tallest bar's total: the chart's scale. */
  max = 0;
  ticks: { left: number; label: string }[] = [];
  loading = false;
  problem: 'unavailable' | 'failed' | null = null;
  /** The bar under the pointer, or picked with the arrow keys. */
  active: number | null = null;

  private request?: Subscription;
  private live?: LiveRefresh;

  ngOnInit(): void {
    this.restore();
    this.resolvePeriod();
    this.load();

    // A period that runs up to now keeps gaining sends: move it along and recount.
    this.live = new LiveRefresh(this.zone, {
      refresh: () => {
        if (this.preset === 'custom' || !reachesNow(this.period)) return;
        this.resolvePeriod();
        this.load(true);
      },
      interval: () => LIVE_MS,
    });
    this.live.start();
    this.destroyRef.onDestroy(() => {
      this.live?.stop();
      this.request?.unsubscribe();
    });
  }

  get periodText(): string {
    return periodLabel(this.period);
  }

  get share(): string {
    if (!this.data?.total) return '';
    const { total, delivered, failed, pending } = this.data;
    return formatShare(deliveredShare({ total, delivered, failed, inFlight: pending }));
  }

  /** The highlighted bar in words, for the line above the chart. */
  get readout(): string {
    const bucket = this.active === null ? null : this.buckets[this.active];
    if (!bucket) return '';
    const pending = this.pendingOf(bucket);
    return (
      `${bucketLabel(bucket.start, this.interval)}: ${formatCount(bucket.total)} sent, ` +
      `${formatCount(bucket.delivered)} delivered, ${formatCount(bucket.failed)} failed` +
      (pending ? `, ${formatCount(pending)} no receipt yet` : '')
    );
  }

  /** The chart in one sentence, for screen readers. */
  get chartSummary(): string {
    const unit = SENT_INTERVALS.find((option) => option.value === this.interval)?.label.toLowerCase() ?? '';
    if (!this.data) return `Messages sent, ${unit}`;
    let busiest: SentBucket | null = null;
    for (const bucket of this.buckets) if (!busiest || bucket.total > busiest.total) busiest = bucket;
    const peak = busiest && busiest.total ? `, most in ${bucketLabel(busiest.start, this.interval)} (${formatCount(busiest.total)})` : '';
    return `Messages sent, ${unit}: ${formatCount(this.data.total)} in all${peak}. Use the arrow keys to read each bar.`;
  }

  fits(interval: SentInterval): boolean {
    return intervalFits(this.period, interval);
  }

  pendingOf(bucket: SentBucket): number {
    return Math.max(0, bucket.total - bucket.delivered - bucket.failed);
  }

  height(bucket: SentBucket): string {
    return this.max ? `${(bucket.total / this.max) * 100}%` : '0%';
  }

  choosePreset(value: string): void {
    const preset = value as SentPreset;
    if (preset === this.preset) return;
    if (preset === 'custom') this.seedCustom();
    this.preset = preset;
    this.resolvePeriod();
    this.save();
    this.load();
  }

  chooseInterval(interval: SentInterval): void {
    if (interval === this.interval || !this.fits(interval)) return;
    this.picked = interval;
    this.interval = interval;
    this.save();
    this.load();
  }

  onCustomChange(field: 'fromDate' | 'fromTime' | 'toDate' | 'toTime', event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    if (field === 'fromDate') this.customFromDate = value;
    if (field === 'fromTime') this.customFromTime = value;
    if (field === 'toDate') this.customToDate = value;
    if (field === 'toTime') this.customToTime = value;
    this.resolvePeriod();
    if (this.rangeError) {
      this.cdr.markForCheck();
      return;
    }
    this.save();
    this.load();
  }

  onChartKey(event: KeyboardEvent): void {
    const count = this.buckets.length;
    if (!count) return;
    const current = this.active ?? -1;
    let next: number;
    switch (event.key) {
      case 'ArrowRight':
        next = Math.min(count - 1, current + 1);
        break;
      case 'ArrowLeft':
        next = current < 0 ? count - 1 : Math.max(0, current - 1);
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = count - 1;
        break;
      case 'Escape':
        this.active = null;
        return;
      default:
        return;
    }
    event.preventDefault();
    this.active = next;
  }

  load(quiet = false): void {
    if (this.preset === 'custom' && this.rangeError) return;
    this.request?.unsubscribe();
    this.loading = true;
    if (!quiet) this.problem = null;
    this.request = this.reports.sentOverview(this.period.from, this.period.to, this.interval).subscribe({
      next: (data) => {
        this.present(data);
        this.loading = false;
        this.problem = null;
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.loading = false;
        // A background recount that fails keeps the numbers already shown.
        if (!quiet || !this.data) this.problem = error?.status === 404 ? 'unavailable' : 'failed';
        this.cdr.markForCheck();
      },
    });
    this.cdr.markForCheck();
  }

  private present(data: SentOverview): void {
    this.data = data;
    this.buckets = data.buckets;
    this.max = this.buckets.reduce((tallest, bucket) => Math.max(tallest, bucket.total), 0);
    if (this.active !== null && this.active >= this.buckets.length) this.active = null;

    // Hours are labelled on round hours (00:00, 06:00, …; midnight shows the date), the rest evenly.
    const count = this.buckets.length;
    let labelled: (index: number) => boolean;
    if (data.interval === 'hour') {
      const hours = [1, 2, 3, 4, 6, 12, 24].find((step) => count / step <= TICKS) ?? 24;
      labelled = (index) => this.buckets[index].start.getHours() % hours === 0;
    } else {
      const step = Math.max(1, Math.ceil(count / TICKS));
      labelled = (index) => index % step === 0;
    }
    this.ticks = [];
    for (let index = 0; index < count; index++) {
      if (!labelled(index)) continue;
      this.ticks.push({ left: ((index + 0.5) / count) * 100, label: axisLabel(this.buckets[index].start, data.interval) });
    }
  }

  /** The period for the preset, or the custom inputs; then the interval that suits it. */
  private resolvePeriod(): void {
    this.rangeError = '';
    if (this.preset === 'custom') {
      const from = fromInputs(this.customFromDate, this.customFromTime);
      const to = fromInputs(this.customToDate, this.customToTime);
      if (!from || !to) {
        this.rangeError = 'Choose a start and an end date.';
        return;
      }
      // Nothing is sent in the future, so a later end stops at now.
      const now = new Date();
      const end = to > now ? now : to;
      if (end <= from) {
        this.rangeError = to > now ? 'The start must be before now.' : 'The end must be after the start.';
        return;
      }
      this.period = { from, to: end };
    } else {
      this.period = presetPeriod(this.preset);
    }
    this.interval = this.picked && intervalFits(this.period, this.picked) ? this.picked : autoInterval(this.period);
  }

  /** A custom range starts as the period on screen. */
  private seedCustom(): void {
    this.customFromDate = dateInput(this.period.from);
    this.customFromTime = timeInput(this.period.from);
    this.customToDate = dateInput(this.period.to);
    this.customToTime = timeInput(this.period.to);
  }

  private restore(): void {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null') as SavedChoice | null;
      if (!saved) return;
      if (SENT_INTERVALS.some((option) => option.value === saved.interval)) this.picked = saved.interval ?? null;
      if (!SENT_PRESETS.some((option) => option.value === saved.preset)) return;
      if (saved.preset === 'custom') {
        const from = new Date(saved.from ?? '');
        const to = new Date(saved.to ?? '');
        if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return;
        this.period = { from, to };
        this.seedCustom();
      }
      this.preset = saved.preset as SentPreset;
    } catch {
      // Storage can be blocked or empty: start from the default.
    }
  }

  private save(): void {
    const choice: SavedChoice = { preset: this.preset, interval: this.picked };
    if (this.preset === 'custom') {
      choice.from = this.period.from.toISOString();
      choice.to = this.period.to.toISOString();
    }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(choice));
    } catch {
      // Not remembered this time.
    }
  }
}
